import { gql } from '@apollo/client';

// ============================================================================
// CURRENCIES
// ============================================================================

export const GET_CURRENCIES = gql`
  query AdminCurrencies {
    adminCurrencies {
      id
      code
      name
      symbol
      decimals
      exchangeRate
      manualRate
      isActive
      isDefault
      lastRateUpdate
    }
  }
`;

export const CREATE_CURRENCY = gql`
  mutation AdminCreateCurrency(
    $code: String!
    $name: String!
    $symbol: String!
    $decimals: Int
    $exchangeRate: Float
    $isActive: Boolean
  ) {
    adminCreateCurrency(
      code: $code
      name: $name
      symbol: $symbol
      decimals: $decimals
      exchangeRate: $exchangeRate
      isActive: $isActive
    ) {
      id
      code
      name
    }
  }
`;

export const UPDATE_CURRENCY = gql`
  mutation AdminUpdateCurrency(
    $id: String!
    $name: String
    $symbol: String
    $exchangeRate: Float
    $isActive: Boolean
  ) {
    adminUpdateCurrency(
      id: $id
      name: $name
      symbol: $symbol
      exchangeRate: $exchangeRate
      isActive: $isActive
    ) {
      id
      code
      name
    }
  }
`;

export const SET_DEFAULT_CURRENCY = gql`
  mutation AdminSetDefaultCurrency($code: String!) {
    adminSetDefaultCurrency(code: $code) {
      id
      code
      isDefault
    }
  }
`;

// ============================================================================
// PLANS
// ============================================================================

export const GET_PLANS = gql`
  query AdminPlans($status: AdminPlanStatus, $category: AdminPlanCategory, $activeOnly: Boolean) {
    adminPlans(status: $status, category: $category, activeOnly: $activeOnly) {
      id
      code
      name
      description
      category
      status
      sortOrder
      isPopular
      monthlyPrice
      yearlyPrice
      yearlyDiscount
      trialDays
      colorPrimary
      icon
      badges
      highlights
      createdAt
      updatedAt
    }
  }
`;

export const GET_PLAN = gql`
  query AdminPlan($id: String!) {
    adminPlan(id: $id) {
      id
      code
      name
      description
      category
      status
      sortOrder
      isPopular
      monthlyPrice
      yearlyPrice
      yearlyDiscount
      trialDays
      colorPrimary
      colorSecondary
      icon
      badges
      highlights
      maxUsers
      migrationToId
      deprecationDate
      createdAt
      updatedAt
      features {
        id
        isIncluded
        value
        tierLevel
        tooltip
        feature {
          id
          code
          name
          description
          category
          valueType
          unit
          icon
        }
      }
      limits {
        id
        value
        isUnlimited
        resetPeriod
        tooltip
        limit {
          id
          code
          name
          description
          category
          unit
          icon
        }
      }
      rewards {
        id
        multiplier
        cashbackPercent
        signupBonus
        referralBonus
        referralPercent
        monthlyBonus
        yearlyBonus
        exclusiveRewards
        priorityAccess
      }
      currencies {
        currencyCode
        currencySymbol
        monthlyPrice
        yearlyPrice
      }
    }
  }
`;

export const CREATE_PLAN = gql`
  mutation AdminCreatePlan(
    $code: String!
    $name: String!
    $monthlyPrice: Float!
    $description: String
    $category: AdminPlanCategory
    $yearlyPrice: Float
    $yearlyDiscount: Int
    $trialDays: Int
    $isPopular: Boolean
    $colorPrimary: String
    $icon: String
    $highlights: [String!]
    $maxUsers: Int
  ) {
    adminCreatePlan(
      code: $code
      name: $name
      monthlyPrice: $monthlyPrice
      description: $description
      category: $category
      yearlyPrice: $yearlyPrice
      yearlyDiscount: $yearlyDiscount
      trialDays: $trialDays
      isPopular: $isPopular
      colorPrimary: $colorPrimary
      icon: $icon
      highlights: $highlights
      maxUsers: $maxUsers
    ) {
      id
      code
      name
    }
  }
`;

export const UPDATE_PLAN = gql`
  mutation AdminUpdatePlan(
    $id: String!
    $name: String
    $description: String
    $monthlyPrice: Float
    $yearlyPrice: Float
    $yearlyDiscount: Int
    $trialDays: Int
    $isPopular: Boolean
    $colorPrimary: String
    $icon: String
    $highlights: [String!]
    $maxUsers: Int
  ) {
    adminUpdatePlan(
      id: $id
      name: $name
      description: $description
      monthlyPrice: $monthlyPrice
      yearlyPrice: $yearlyPrice
      yearlyDiscount: $yearlyDiscount
      trialDays: $trialDays
      isPopular: $isPopular
      colorPrimary: $colorPrimary
      icon: $icon
      highlights: $highlights
      maxUsers: $maxUsers
    ) {
      id
      code
      name
    }
  }
`;

export const UPDATE_PLAN_STATUS = gql`
  mutation AdminUpdatePlanStatus($id: String!, $status: AdminPlanStatus!) {
    adminUpdatePlanStatus(id: $id, status: $status) {
      id
      code
      status
    }
  }
`;

// ============================================================================
// FEATURE/LIMIT DEFINITIONS
// ============================================================================

export const GET_FEATURE_DEFINITIONS = gql`
  query AdminFeatureDefinitions {
    adminFeatureDefinitions {
      id
      code
      name
      description
      category
      valueType
      unit
      sortOrder
      isPublic
      icon
    }
  }
`;

export const GET_LIMIT_DEFINITIONS = gql`
  query AdminLimitDefinitions {
    adminLimitDefinitions {
      id
      code
      name
      description
      category
      unit
      defaultValue
      sortOrder
      isPublic
      icon
    }
  }
`;

// ============================================================================
// PLAN FEATURES/LIMITS/REWARDS
// ============================================================================

export const SET_PLAN_FEATURE = gql`
  mutation AdminSetPlanFeature(
    $planId: String!
    $featureId: String!
    $isIncluded: Boolean!
    $value: String
    $tierLevel: Int
    $tooltip: String
  ) {
    adminSetPlanFeature(
      planId: $planId
      featureId: $featureId
      isIncluded: $isIncluded
      value: $value
      tierLevel: $tierLevel
      tooltip: $tooltip
    )
  }
`;

export const SET_PLAN_LIMIT = gql`
  mutation AdminSetPlanLimit(
    $planId: String!
    $limitId: String!
    $value: Int!
    $isUnlimited: Boolean
    $resetPeriod: String
    $tooltip: String
  ) {
    adminSetPlanLimit(
      planId: $planId
      limitId: $limitId
      value: $value
      isUnlimited: $isUnlimited
      resetPeriod: $resetPeriod
      tooltip: $tooltip
    )
  }
`;

export const SET_PLAN_REWARDS = gql`
  mutation AdminSetPlanRewards(
    $planId: String!
    $multiplier: Float
    $cashbackPercent: Float
    $signupBonus: Int
    $referralBonus: Int
    $referralPercent: Float
    $monthlyBonus: Int
    $yearlyBonus: Int
    $exclusiveRewards: [String!]
    $priorityAccess: Boolean
  ) {
    adminSetPlanRewards(
      planId: $planId
      multiplier: $multiplier
      cashbackPercent: $cashbackPercent
      signupBonus: $signupBonus
      referralBonus: $referralBonus
      referralPercent: $referralPercent
      monthlyBonus: $monthlyBonus
      yearlyBonus: $yearlyBonus
      exclusiveRewards: $exclusiveRewards
      priorityAccess: $priorityAccess
    ) {
      id
      multiplier
      cashbackPercent
    }
  }
`;

export const SET_PLAN_CURRENCY_PRICE = gql`
  mutation AdminSetPlanCurrencyPrice(
    $planId: String!
    $currencyCode: String!
    $monthlyPrice: Float!
    $yearlyPrice: Float
  ) {
    adminSetPlanCurrencyPrice(
      planId: $planId
      currencyCode: $currencyCode
      monthlyPrice: $monthlyPrice
      yearlyPrice: $yearlyPrice
    )
  }
`;

// ============================================================================
// SUBSCRIPTIONS
// ============================================================================

export const GET_SUBSCRIPTIONS = gql`
  query AdminSubscriptions(
    $status: AdminSubscriptionStatus
    $planId: String
    $userId: String
    $enterpriseId: String
    $expiringBefore: Timestamp
    $page: Int
    $pageSize: Int
  ) {
    adminSubscriptions(
      status: $status
      planId: $planId
      userId: $userId
      enterpriseId: $enterpriseId
      expiringBefore: $expiringBefore
      page: $page
      pageSize: $pageSize
    ) {
      items {
        id
        status
        billingPeriod
        startDate
        endDate
        trialEndDate
        nextBillingDate
        priceAmount
        priceCurrency
        autoRenew
        cancelledAt
        createdAt
        userId
        enterpriseId
        plan {
          id
          code
          name
          colorPrimary
        }
        user {
          id
          email
        }
        enterprise {
          id
          name
        }
      }
      total
      page
      pageSize
    }
  }
`;

export const GET_SUBSCRIPTION = gql`
  query AdminSubscription($id: String!) {
    adminSubscription(id: $id) {
      id
      status
      billingPeriod
      startDate
      endDate
      trialEndDate
      nextBillingDate
      priceAmount
      priceCurrency
      autoRenew
      discountPercent
      discountCode
      paymentMethod
      cancelledAt
      createdAt
      updatedAt
      userId
      enterpriseId
      plan {
        id
        code
        name
        monthlyPrice
        yearlyPrice
        colorPrimary
      }
      user {
        id
        email
      }
      enterprise {
        id
        name
      }
      payments {
        id
        amount
        currency
        status
        provider
        providerTxnId
        paidAt
        createdAt
      }
      history {
        id
        action
        fromStatus
        toStatus
        reason
        performedBy
        createdAt
      }
    }
  }
`;

export const GET_SUBSCRIPTION_STATS = gql`
  query AdminSubscriptionStats {
    adminSubscriptionStats {
      total
      byStatus
      byPlan {
        planCode
        count
      }
      mrr
      arr
    }
  }
`;

export const CREATE_SUBSCRIPTION = gql`
  mutation AdminCreateSubscription(
    $planCode: String!
    $userId: String
    $enterpriseId: String
    $billingPeriod: AdminBillingPeriod
    $currencyCode: String
    $discountPercent: Float
    $discountCode: String
    $paymentMethod: String
    $skipTrial: Boolean
  ) {
    adminCreateSubscription(
      planCode: $planCode
      userId: $userId
      enterpriseId: $enterpriseId
      billingPeriod: $billingPeriod
      currencyCode: $currencyCode
      discountPercent: $discountPercent
      discountCode: $discountCode
      paymentMethod: $paymentMethod
      skipTrial: $skipTrial
    ) {
      id
      status
    }
  }
`;

export const CANCEL_SUBSCRIPTION = gql`
  mutation AdminCancelSubscription($id: String!, $reason: String, $immediate: Boolean) {
    adminCancelSubscription(id: $id, reason: $reason, immediate: $immediate) {
      id
      status
      cancelledAt
    }
  }
`;

export const REACTIVATE_SUBSCRIPTION = gql`
  mutation AdminReactivateSubscription($id: String!) {
    adminReactivateSubscription(id: $id) {
      id
      status
    }
  }
`;

export const EXTEND_SUBSCRIPTION = gql`
  mutation AdminExtendSubscription($id: String!, $days: Int!, $reason: String) {
    adminExtendSubscription(id: $id, days: $days, reason: $reason) {
      id
      endDate
    }
  }
`;

export const UPGRADE_SUBSCRIPTION = gql`
  mutation AdminUpgradeSubscription($id: String!, $toPlanCode: String!, $immediate: Boolean) {
    adminUpgradeSubscription(id: $id, toPlanCode: $toPlanCode, immediate: $immediate) {
      id
      status
      plan {
        id
        code
        name
      }
    }
  }
`;

export const DOWNGRADE_SUBSCRIPTION = gql`
  mutation AdminDowngradeSubscription($id: String!, $toPlanCode: String!) {
    adminDowngradeSubscription(id: $id, toPlanCode: $toPlanCode) {
      id
      status
      plan {
        id
        code
        name
      }
    }
  }
`;
